import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import AdminLayout from '../../layouts/AdminLayout';
import DataTable, { type Column } from '../../components/admin/DataTable';
import { since } from '../../components/admin/format';

type Summary = {
  today_cost: number; today_calls: number;
  window_cost: number; window_calls: number;
  own_cost: number; system_cost: number; system_share: number;
  own_users: number; system_users: number;
  capped_today: number; capped_window: number;
};

type SystemKey = {
  configured: boolean;
  error?: string;
  provider?: string | null;
  model_id?: string | null;
  api_base?: string | null;
  key_hint?: string | null;
  daily_cap: number;
  max_output_tokens: number;
  max_prompt_chars: number;
  key_source: 'env' | 'credentials' | 'credentials_legacy' | null;
  cap_source: 'env' | 'admin' | 'credentials' | 'default';
  cap_changed_by: string | null;
  cap_changed_at: string | null;
  usd_cny_rate: number;
  cost_trackable: boolean;
};

type UserRow = {
  user_id: number; email: string; calls: number; cost: number;
  own_cost: number; system_cost: number; uses_own_key: boolean;
  today_system_cost: number; last_at: string | null;
};

type FeatureRow = {
  feature: string; calls: number; cost: number; avg_cost: number;
  avg_input: number; avg_output: number; failure_rate: number;
};

type LedgerRow = {
  id: number; at: string; email: string; feature: string;
  provider: string; model_id: string;
  input_tokens: number | null; output_tokens: number | null;
  cost: number | null; cost_amount: number | null; cost_currency: string | null;
  key_source: 'own' | 'system';
  outcome: 'succeeded' | 'capped' | 'failed'; error_code: string | null;
};

type Props = {
  tab: 'by-user' | 'by-feature' | 'ledger';
  summary: Summary;
  daily_series: { date: string; own: number; system: number }[];
  system_key: SystemKey;
  table: { rows: UserRow[] | FeatureRow[] | LedgerRow[]; page: number; per: number; total: number };
};

type AiUsageComponent = React.FC<Props> & { layout?: (page: React.ReactNode) => React.ReactNode };

const usd = (value: number | null, digits = 2) =>
  value === null ? '—' : `$${value.toFixed(digits)}`;

// 原币金额。千问在阿里那边按人民币计价 —— 只显示折算后的美元，等于告诉用户
// 一个他在阿里账单上找不到的数字。
const SYMBOL: Record<string, string> = { USD: '$', CNY: '¥' };
const native = (amount: number | null, currency: string | null, digits = 4) =>
  amount === null || currency === null ? '—' : `${SYMBOL[currency] ?? ''}${amount.toFixed(digits)}`;

const clock = (iso: string) => new Date(iso).toTimeString().slice(0, 8);

const TABS: [Props['tab'], string][] = [
  ['by-user', '按用户'],
  ['by-feature', '按动作'],
  ['ledger', '明细'],
];

const SOURCE_LABEL: Record<string, string> = {
  env: 'ENV',
  admin: '后台',
  credentials: 'credentials',
  credentials_legacy: 'credentials（遗产 openai 段）',
  default: '内置默认值',
};

// 30 天堆叠柱。两色对应两条 key 来源，与前台 /settings 用的是同一组
// token（ai.own / ai.sys）—— 同一个人在两处看到的同一件事，不该要他记两套映射。
function Chart({ series }: { series: Props['daily_series'] }) {
  const max = Math.max(0.0001, ...series.map((d) => d.own + d.system));
  const width = 620;
  const gap = 3;
  const bar = (width - gap * (series.length - 1)) / series.length;
  const base = 98;

  return (
    <svg viewBox={`0 0 ${width} 108`} preserveAspectRatio="none" className="block h-[108px] w-full">
      <line x1="0" y1={base} x2={width} y2={base} className="stroke-admin-line" strokeWidth="1" />
      {series.map((d, i) => {
        const x = i * (bar + gap);
        const own = Math.round((d.own / max) * (base - 6));
        const sys = Math.round((d.system / max) * (base - 6));
        return (
          <g key={d.date}>
            {sys > 0 && <rect x={x} y={base - sys} width={bar} height={sys} rx="1" className="fill-ai-sys" />}
            {own > 0 && (
              <rect x={x} y={base - sys - own} width={bar} height={own} rx="1" className="fill-ai-own" />
            )}
          </g>
        );
      })}
    </svg>
  );
}

function Stat({ label, value, detail, tone }: {
  label: string; value: string; detail: string; tone?: 'own' | 'sys' | 'warn';
}) {
  const toneClass =
    tone === 'own' ? 'text-ai-own' : tone === 'sys' ? 'text-ai-sys' : tone === 'warn' ? 'text-admin-warn' : '';
  return (
    <div className="rounded-lg border border-admin-line bg-admin-surface px-3 py-2.5 shadow-sm">
      <div className="mb-0.5 text-[11.5px] text-admin-muted">{label}</div>
      <div className={`text-[23px] font-semibold tabular-nums tracking-tight ${toneClass}`}>{value}</div>
      <div className="mt-0.5 text-[11.5px] text-admin-muted">{detail}</div>
    </div>
  );
}

// 走系统额度的用户显示进度条；配了自己 key 的显示 "—"。
// **不能写 0** —— 那会读成「今天用了 0 块」，而事实是他根本不在这条闸门的
// 管辖范围内。
function TodayCell({ row, cap }: { row: UserRow; cap: number }) {
  if (row.uses_own_key) {
    return (
      <span className="text-admin-muted">
        — <span className="text-[11px]">（走自己的 key）</span>
      </span>
    );
  }
  const ratio = cap > 0 ? Math.min(1, row.today_system_cost / cap) : 1;
  const full = ratio >= 1;
  return (
    <span className="flex items-center gap-1.5">
      <span className="inline-block h-[5px] w-[62px] overflow-hidden rounded-full border border-admin-line bg-admin-surface2">
        <span
          className={`block h-full ${full ? 'bg-admin-warn' : 'bg-ai-sys'}`}
          style={{ width: `${ratio * 100}%` }}
        />
      </span>
      <span className={`tabular-nums ${full ? 'text-admin-warn' : 'text-admin-muted'}`}>
        {usd(row.today_system_cost)} / {usd(cap)}
      </span>
    </span>
  );
}

const OUTCOME_PILL: Record<LedgerRow['outcome'], [string, string]> = {
  succeeded: ['成功', 'bg-admin-okSoft text-admin-ok'],
  capped: ['被上限拦住', 'bg-admin-warnSoft text-admin-warn'],
  failed: ['失败', 'bg-admin-dangerSoft text-admin-danger'],
};

function userColumns(cap: number): Column<UserRow>[] {
  return [
    { key: 'email', header: '用户', truncate: true, render: (r) => r.email },
    { key: 'calls', header: '调用', width: '78px', align: 'right', render: (r) => r.calls.toLocaleString() },
    { key: 'cost', header: '成本', width: '88px', align: 'right', render: (r) => usd(r.cost) },
    {
      key: 'own', header: '自己的 key', width: '104px', align: 'right',
      render: (r) => (r.own_cost > 0 ? <span className="text-ai-own">{usd(r.own_cost)}</span> : <span className="text-admin-muted">—</span>),
    },
    {
      key: 'system', header: '系统额度', width: '104px', align: 'right',
      render: (r) => (r.system_cost > 0 ? <span className="text-ai-sys">{usd(r.system_cost)}</span> : <span className="text-admin-muted">—</span>),
    },
    { key: 'today', header: '今日额度', width: '190px', render: (r) => <TodayCell row={r} cap={cap} /> },
    { key: 'last', header: '最近一次', width: '96px', render: (r) => since(r.last_at) },
  ];
}

const FEATURE_COLUMNS: Column<FeatureRow>[] = [
  { key: 'feature', header: '动作', truncate: true, render: (r) => <code className="font-mono text-[12px]">{r.feature}</code> },
  { key: 'calls', header: '调用', width: '88px', align: 'right', render: (r) => r.calls.toLocaleString() },
  { key: 'cost', header: '成本', width: '88px', align: 'right', render: (r) => usd(r.cost) },
  { key: 'avg', header: '均价 / 次', width: '104px', align: 'right', render: (r) => usd(r.avg_cost, 4) },
  {
    key: 'tokens', header: '均 token 进→出', width: '124px', align: 'right',
    render: (r) => `${r.avg_input.toLocaleString()} → ${r.avg_output.toLocaleString()}`,
  },
  {
    key: 'fail', header: '失败率', width: '78px', align: 'right',
    render: (r) => (
      <span className={r.failure_rate > 10 ? 'text-admin-warn' : ''}>{r.failure_rate}%</span>
    ),
  },
];

const LEDGER_COLUMNS: Column<LedgerRow>[] = [
  { key: 'at', header: '时间', width: '74px', mono: true, render: (r) => clock(r.at) },
  { key: 'email', header: '用户', truncate: true, render: (r) => r.email },
  { key: 'feature', header: '动作', width: '132px', mono: true, render: (r) => r.feature },
  {
    key: 'model', header: '供应商 · 模型', width: '196px', mono: true, truncate: true,
    render: (r) => `${r.provider} · ${r.model_id}`,
  },
  {
    key: 'tokens', header: 'token 进→出', width: '112px', align: 'right', mono: true,
    render: (r) =>
      r.input_tokens === null
        ? <span className="text-admin-muted">—</span>
        : `${r.input_tokens.toLocaleString()} → ${(r.output_tokens ?? 0).toLocaleString()}`,
  },
  {
    key: 'cost', header: '成本', width: '116px', align: 'right',
    // 三件事在这一列里：
    //   1. NULL 与 0 必须看得出区别 —— NULL 是算不出这个模型的价，
    //      0 是真的没花钱（被拦住）。
    //   2. **原币在前，美元在后**。千问在阿里那边按人民币结算，
    //      只显示折算后的美元等于给一个他在账单上找不到的数字。
    //   3. 原币就是美元时不重复显示两遍。
    render: (r) => {
      if (r.cost === null) {
        return <span className="text-admin-muted" title="算不出这个模型的价">— 无价格</span>;
      }
      if (r.cost_currency && r.cost_currency !== 'USD') {
        return (
          <span title={`按写入时的汇率折算约 ${usd(r.cost, 4)}`}>
            {native(r.cost_amount, r.cost_currency)}
            <span className="ml-1 text-[11px] text-admin-muted">≈{usd(r.cost, 4)}</span>
          </span>
        );
      }
      return usd(r.cost, 4);
    },
  },
  {
    key: 'key', header: 'key', width: '84px',
    render: (r) => (
      <span className={`rounded px-1.5 py-px text-[11px] font-semibold ${
        r.key_source === 'own' ? 'bg-ai-ownSoft text-ai-own' : 'bg-ai-sysSoft text-ai-sys'
      }`}>
        {r.key_source === 'own' ? '自己的' : '系统'}
      </span>
    ),
  },
  {
    key: 'outcome', header: '结果', width: '150px',
    render: (r) => {
      const [label, cls] = OUTCOME_PILL[r.outcome];
      return (
        <span className={`rounded px-1.5 py-px text-[11px] font-semibold ${cls}`}>
          {r.error_code ? `${r.error_code}` : label}
        </span>
      );
    },
  },
];

// 每日上限的编辑框 —— 后台唯一一处写入。
//
// 两件事必须显示在这儿，不能只写在文档里：
//   1. ENV 顶掉后台时，改了不生效。优先级是 ENV > 后台 > credentials > 默认，
//      这是刻意的（ENV 是运维的紧急口子）。不说的话就是个假输入框。
//   2. 上次是谁改的、什么时候 —— 后台开写入口的条件就是留痕，
//      留了痕不显示等于没留。
// 后台唯一一处写入：日成本上限 + 美元兑人民币汇率。
//
// 三件事必须显示在这儿，不能只写在文档里：
//   1. ENV 顶掉后台时，上限改了不生效。优先级是 ENV > 后台 > credentials > 默认，
//      这是刻意的（ENV 是运维的紧急口子）。不说的话就是个假输入框。
//   2. 上次是谁改的、什么时候 —— 后台开写入口的条件就是留痕，留了不显示等于没留。
//   3. 汇率是干什么用的 —— 它只影响跨币种合计的显示，不影响闸门
//      （闸门只管系统 key，而系统 key 走 OpenRouter，本来就是美元）。
function SettingsEditor({ systemKey }: { systemKey: SystemKey }) {
  const [cap, setCap] = useState(systemKey.daily_cap.toFixed(2));
  const [rate, setRate] = useState(systemKey.usd_cny_rate.toFixed(4));
  const [saving, setSaving] = useState(false);
  const shadowed = systemKey.cap_source === 'env';

  const capDirty = cap !== systemKey.daily_cap.toFixed(2);
  const rateDirty = rate !== systemKey.usd_cny_rate.toFixed(4);

  const save = () => {
    const payload: Record<string, string> = {};
    if (capDirty) payload.daily_cost_cap_usd = cap;
    if (rateDirty) payload.usd_cny_rate = rate;
    if (Object.keys(payload).length === 0) return;

    setSaving(true);
    router.patch('/admin/ai-settings', payload, {
      preserveScroll: true,
      onFinish: () => setSaving(false),
    });
  };

  const field = (
    prefix: string,
    value: string,
    onChange: (v: string) => void,
  ) => (
    <span className="flex items-center gap-1">
      <span className="text-admin-muted">{prefix}</span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && save()}
        className="w-20 rounded border border-admin-line2 bg-admin-surface px-1.5 py-0.5 text-[12.5px] tabular-nums text-admin-ink focus:border-admin-accent focus:outline-none"
      />
    </span>
  );

  return (
    <div className="mt-3 rounded-md border border-admin-line bg-admin-surface2 px-3 py-2.5">
      <div className="mb-2 text-[11.5px] font-semibold text-admin-ink2">后台可改</div>

      <div className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 text-[12.5px]">
        <span className="text-admin-muted">每人每日上限</span>
        {field('$', cap, setCap)}

        <span className="text-admin-muted">汇率 USD→CNY</span>
        <span className="flex items-center gap-2">
          {field('¥', rate, setRate)}
          <span className="text-[11px] text-admin-muted">只用于把人民币计价的调用折成美元合计</span>
        </span>
      </div>

      <div className="mt-2.5 flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={(!capDirty && !rateDirty) || saving}
          className="rounded border border-admin-line2 px-2 py-0.5 text-[11.5px] disabled:opacity-40 enabled:hover:border-admin-accent enabled:hover:text-admin-accent"
        >
          {saving ? '保存中' : '保存'}
        </button>
        {systemKey.cap_changed_by && (
          <span className="text-[11.5px] text-admin-muted">
            上次由 {systemKey.cap_changed_by}
            {systemKey.cap_changed_at && ` · ${since(systemKey.cap_changed_at)}`} 改过
          </span>
        )}
      </div>

      {shadowed && (
        <p className="mt-2 text-[11.5px] text-admin-warn">
          上限现在生效的是 <code className="font-mono">AI_DAILY_COST_CAP_USD</code> 这个 ENV，
          改这里<b>不会生效</b>（但会记进修改记录）。
        </p>
      )}
    </div>
  );
}

const AiUsage: AiUsageComponent = ({ tab, summary, daily_series, system_key, table }) => {
  const go = (next: Props['tab']) => router.get('/admin/ai-usage', { tab: next }, { preserveState: false });

  return (
    <>
      <h1 className="mb-3 text-[14.5px] font-semibold tracking-tight">
        <span className="font-normal text-admin-muted">系统 / </span>AI 用量
      </h1>

      <div className="mb-3.5 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        <Stat label="今日成本" value={usd(summary.today_cost)} detail={`${summary.today_calls} 次`} />
        <Stat label="近 30 天成本" value={usd(summary.window_cost)} detail={`${summary.window_calls.toLocaleString()} 次调用`} />
        <Stat
          label="系统赠送额度承担"
          value={usd(summary.system_cost)}
          detail={`占 ${summary.system_share}% · ${summary.system_users} 个用户`}
          tone="sys"
        />
        <Stat
          label="今天撞上限"
          value={String(summary.capped_today)}
          detail={`人 · 近 30 天 ${summary.capped_window} 人次`}
          tone={summary.capped_today > 0 ? 'warn' : undefined}
        />
      </div>

      <div className="mb-3.5 grid gap-3 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div className="overflow-hidden rounded-lg border border-admin-line bg-admin-surface shadow-sm">
          <div className="flex items-center gap-2 border-b border-admin-line bg-admin-surface2 px-3 py-2 text-[12.5px] font-semibold">
            近 30 天每日成本
            <span className="flex-1" />
            <span className="text-[11.5px] font-normal text-admin-muted">按 key 来源堆叠</span>
          </div>
          <div className="p-3">
            <Chart series={daily_series} />
            <div className="mt-2 flex items-center gap-3.5 text-[11.5px] text-admin-muted">
              <span><i className="mr-1.5 inline-block h-2 w-2 rounded-sm bg-ai-own align-[-1px]" />用户自己的 key {usd(summary.own_cost)}</span>
              <span><i className="mr-1.5 inline-block h-2 w-2 rounded-sm bg-ai-sys align-[-1px]" />系统赠送额度 {usd(summary.system_cost)}</span>
            </div>
          </div>
        </div>

        {/* 系统 key 状态卡。**两行「来源」是整张卡的运维价值所在**：
            AI 没生效时第一个要看的就是系统 key 到底从哪读到、有没有读到。 */}
        <div className="overflow-hidden rounded-lg border border-admin-line bg-admin-surface shadow-sm">
          <div className="flex items-center gap-2 border-b border-admin-line bg-admin-surface2 px-3 py-2 text-[12.5px] font-semibold">
            系统 AI key
            <span className="flex-1" />
            <span className={`rounded px-1.5 py-px text-[11px] font-semibold ${
              system_key.configured ? 'bg-admin-okSoft text-admin-ok' : 'bg-admin-surface2 text-admin-muted'
            }`}>
              {system_key.configured ? '已配置' : '未配置'}
            </span>
          </div>
          <div className="p-3">
            <dl className="grid grid-cols-[104px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[12.5px]">
              {system_key.error && (
                <>
                  <dt className="text-admin-muted">配置错误</dt>
                  <dd className="text-admin-danger">{system_key.error}</dd>
                </>
              )}
              <dt className="text-admin-muted">供应商</dt>
              <dd>{system_key.provider ?? '—'}</dd>
              <dt className="text-admin-muted">模型</dt>
              <dd className="break-all font-mono text-[11.5px]">{system_key.model_id ?? '—'}</dd>
              {system_key.api_base && (
                <>
                  <dt className="text-admin-muted">API 地址</dt>
                  <dd className="break-all font-mono text-[11.5px]">{system_key.api_base}</dd>
                </>
              )}
              <dt className="text-admin-muted">Key</dt>
              <dd className="break-all font-mono text-[11.5px]">{system_key.key_hint ?? '—'}</dd>
              <dt className="text-admin-muted">每人每日上限</dt>
              <dd><b>{usd(system_key.daily_cap)}</b></dd>
              <dt className="text-admin-muted">输出硬顶</dt>
              <dd className="font-mono text-[11.5px]">{system_key.max_output_tokens.toLocaleString()} token</dd>
              <dt className="text-admin-muted">输入截断</dt>
              <dd className="font-mono text-[11.5px]">
                {system_key.max_prompt_chars.toLocaleString()} 字符
                <span className="ml-1 font-sans text-admin-muted">（≈12k–24k token）</span>
              </dd>
              <dt className="text-admin-muted">Key 来源</dt>
              <dd>
                <span className="rounded bg-admin-accentSoft px-1.5 py-px text-[11px] font-semibold text-admin-accent">
                  {system_key.key_source ? SOURCE_LABEL[system_key.key_source] : '未读到'}
                </span>
              </dd>
              <dt className="text-admin-muted">上限来源</dt>
              <dd>
                <span className="rounded bg-admin-accentSoft px-1.5 py-px text-[11px] font-semibold text-admin-accent">
                  {SOURCE_LABEL[system_key.cap_source]}
                </span>
              </dd>
            </dl>
            {system_key.configured && !system_key.cost_trackable && (
              <div className="mt-3 rounded-md border border-admin-danger bg-admin-dangerSoft px-3 py-2 text-[12px] leading-relaxed text-admin-ink2">
                <b className="text-admin-danger">⚠ 这个模型算不出价，日上限对它完全无效。</b>
                <br />
                成本三列会存 NULL，而 NULL 加起来是 0 —— 闸门永远不触发，
                系统 key 的花费没有上限。要么换一个
                <code className="font-mono">Ai::Models::DASHSCOPE_PRICES_CNY_PER_MILLION</code>
                里有价的模型，要么把这个模型的官方价抄进那张表。
              </div>
            )}
            <SettingsEditor systemKey={system_key} />
            <div className="mt-3 rounded-md border border-admin-line bg-admin-surface2 px-3 py-2 text-[12px] leading-relaxed text-admin-muted">
              <b className="text-admin-ink2">上面两个值可以在这里改</b>，改一次记一行（<code className="font-mono">ai_settings</code> 是
              append-only 的修改记录，所以「谁在什么时候改成多少」查得到）。
              其余的值要 <code className="font-mono">rails credentials:edit</code> 后重新部署。
              所有值都<b className="text-admin-ink2">可被同名 ENV 覆盖</b>
              （照 <code className="font-mono">AdminRoster</code> 的两来源写法），
              上面「来源」两行显示的就是当前<b className="text-admin-ink2">实际生效</b>的那一个。
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3 flex gap-0.5 border-b border-admin-line">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => go(key)}
            className={`border-b-2 px-2.5 py-1.5 text-[12.5px] ${
              tab === key
                ? 'border-admin-accent font-semibold text-admin-accent'
                : 'border-transparent text-admin-muted hover:text-admin-ink'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'ledger' && (
        <div className="mb-3 rounded-md border border-admin-line bg-admin-surface2 px-3 py-2 text-[12px] leading-relaxed text-admin-muted">
          <b className="text-admin-ink2"><code className="font-mono">ai_usages</code> 是 append-only</b>
          {' '}—— 没有 update、没有 destroy，照 worklog 那条规矩（一个能改自己账目的账本没有可信度）。
          所以<b className="text-admin-ink2">失败与被上限拦住的调用照样占一行</b>：
          否则「今天额度掉了却没产出」这种事查不出来。
        </div>
      )}

      {tab === 'by-user' && (
        <DataTable
          columns={userColumns(system_key.daily_cap)}
          rows={table.rows as UserRow[]}
          rowKey={(r) => r.user_id}
          empty="近 30 天没有任何 AI 调用"
          pagination={{ page: table.page, per: table.per, total: table.total }}
        />
      )}

      {tab === 'by-feature' && (
        <>
          <DataTable
            columns={FEATURE_COLUMNS}
            rows={table.rows as FeatureRow[]}
            rowKey={(r) => r.feature}
            empty="近 30 天没有任何 AI 调用"
          />
          <div className="mt-3 max-w-[94ch] rounded-md border border-dashed border-admin-line2 bg-admin-surface2 px-3 py-2 text-[12px] leading-relaxed text-admin-muted">
            本期只有两行，而这正是要说的事。<code className="font-mono">connection_test</code>{' '}
            的失败率高不是 bug —— 那是设置页的「测试并保存」在拦配错的 key，拦住的就该失败。
            <code className="font-mono">ai_tasker</code> 是 <code className="font-mono">/ai-tasker</code>{' '}
            切到 <code className="font-mono">Ai::Gateway</code> 的结果：它此前已经在花这笔钱，
            只是没有任何一行数据记着。动作侧后续接入时这张表按{' '}
            <code className="font-mono">feature</code> 自动长行，不用改这一页。
          </div>
        </>
      )}

      {tab === 'ledger' && (
        <DataTable
          columns={LEDGER_COLUMNS}
          rows={table.rows as LedgerRow[]}
          rowKey={(r) => r.id}
          empty="还没有任何调用记录"
          pagination={{ page: table.page, per: table.per, total: table.total }}
        />
      )}

      <div className="mt-3 max-w-[94ch] rounded-md border border-dashed border-admin-line2 bg-admin-surface2 px-3 py-2 text-[12px] leading-relaxed text-admin-muted">
        这一页刻意<b className="text-admin-ink2">没有</b>「改上限」的输入框：那个数字在 credentials 里，
        改了要重新部署才生效，而一个「改了看起来生效、实际没生效」的输入框比没有更糟。
        也没有按用户单独调额度（那需要一张 override 表 + 一套写入审计，
        而现在还不知道有没有人真的需要它），没有告警（
        <Link href="/admin" className="text-admin-accent hover:underline">§0 规矩 3</Link>
        「界面只呈现已经发生的事实」在后台同样成立）。
      </div>
    </>
  );
};

AiUsage.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>;

export default AiUsage;
